import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import toast from 'react-hot-toast';
import Card from '../ui/Card';
import Button from '../ui/Button';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import formatOrderId from '../../utils/formatOrderId';

const { FiCheckCircle, FiDownload, FiPrinter, FiShare2, FiArrowRight } = FiIcons;

const PaymentReceipt = ({ order }) => {
  const navigate = useNavigate();
  const receiptRef = useRef(null);
  
  if (!order) return null;
  
  const handlePrint = () => {
    window.print();
  };
  
  const handleDownload = async () => {
    if (!receiptRef.current) {
      toast.error('Unable to generate PDF');
      return;
    }
    
    try {
      // Show loading message
      const loadingToast = toast.loading('Generating PDF...');
      
      // Use html2canvas to capture the receipt as an image
      const canvas = await html2canvas(receiptRef.current, {
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true
      });
      
      // Calculate dimensions to fit the receipt in PDF
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      let heightLeft = imgHeight;
      let position = 0;
      
      // Add image to PDF
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      // Add additional pages if receipt is longer than one page
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Save PDF
      pdf.save(`How2Apply_Receipt_${order.orderId}.pdf`);
      toast.dismiss(loadingToast);
      toast.success('Receipt downloaded successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to download receipt. Please try again.');
    }
  };
  
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `How2Apply Receipt - ${order.orderId}`,
        text: `My payment receipt for ${order.serviceType}`,
        url: window.location.href,
      })
      .catch((error) => console.log('Error sharing', error));
    } else {
      alert('Sharing is not available on this device');
    }
  };
  
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const handleTrackApplication = () => {
    console.log("PaymentReceipt: handleTrackApplication called");
    console.log("PaymentReceipt: order =", order);
    console.log("PaymentReceipt: order.id =", order?.id);
    
    if (order && order.id) {
      console.log("PaymentReceipt: Navigating to track-order with ID:", order.id);
      navigate(`/track-order/${order.id}`);
    } else {
      console.log("PaymentReceipt: Order ID not found, showing error");
      toast.error('Unable to track application. Order ID not found.');
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="print:shadow-none"
      >
        <Card className="p-8 print:shadow-none print:border-none">
          <div ref={receiptRef}>
            <div className="flex justify-between items-center mb-6 print:mb-8">
              <div className="flex items-center">
                <div className="bg-green-100 p-3 rounded-full">
                  <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 ml-3">Payment Successful</h2>
              </div>
              <div className="print:hidden flex space-x-2">
                <button 
                  onClick={handlePrint}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full"
                >
                  <SafeIcon icon={FiPrinter} className="w-5 h-5" />
                </button>
                <button 
                  onClick={handleDownload}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full"
                >
                  <SafeIcon icon={FiDownload} className="w-5 h-5" />
                </button>
                <button 
                  onClick={handleShare}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full"
                >
                  <SafeIcon icon={FiShare2} className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="border-t border-b border-gray-200 py-6 mb-6 print:border-black">
              <div className="flex justify-between mb-4">
                <div>
                  <p className="text-sm text-gray-600">Order ID</p>
                  <p className="font-medium text-gray-900 font-mono">{formatOrderId(order.orderId, { date: order.paymentDate })}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Payment Date</p>
                  <p className="font-medium text-gray-900">{formatDate(order.paymentDate)}</p>
                </div>
              </div>
              <div className="flex justify-between">
                <div>
                  <p className="text-sm text-gray-600">Payment Method</p>
                  <p className="font-medium text-gray-900 capitalize">{order.paymentMethod}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Status</p>
                  <p className="font-medium text-green-600">Paid</p>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Service Details</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between mb-2">
                  <p className="text-gray-700">Service Name</p>
                  <p className="font-medium text-gray-900">{order.serviceName || order.serviceType}</p>
                </div>
                <div className="flex justify-between mb-2">
                  <p className="text-gray-700">Category</p>
                  <p className="font-medium text-gray-900">{order.category}</p>
                </div>
                <div className="flex justify-between mb-2">
                  <p className="text-gray-700">Service Type</p>
                  <p className="font-medium text-gray-900 capitalize">{order.serviceType} Service</p>
                </div>
                <div className="flex justify-between mb-2">
                  <p className="text-gray-700">Processing Time</p>
                  <p className="font-medium text-gray-900">{order.processingTime || order.estimatedProcessingTime || '2-4 hours'}</p>
                </div>
                {order.lastDate && (
                  <div className="flex justify-between">
                    <p className="text-gray-700">Last Date</p>
                    <p className="font-medium text-gray-900">{order.lastDate}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Summary</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <p className="text-gray-700">Exam Fee</p>
                  <p className="text-gray-900">₹{order.examFee?.toFixed(2) || '0.00'}</p>
                </div>
                
                {order.useMembership ? (
                  <div className="flex justify-between">
                    <p className="text-gray-700">Service Fee</p>
                    <div className="flex items-center">
                      <span className="line-through text-gray-400 mr-2">₹{order.serviceFee?.toFixed(2) || '0.00'}</span>
                      <span className="text-green-600">₹0.00</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between">
                    <p className="text-gray-700">Service Fee</p>
                    <p className="text-gray-900">₹{order.serviceFee?.toFixed(2) || '0.00'}</p>
                  </div>
                )}
                
                <div className="flex justify-between">
                  <p className="text-gray-700">Bank Charges and Taxes</p>
                  <p className="text-gray-900">₹{order.bankCharge?.toFixed(2) || '0.00'}</p>
                </div>
                
                <div className="border-t border-gray-200 pt-2 mt-2 flex justify-between">
                  <p className="font-semibold text-gray-900">Total Amount</p>
                  <p className="font-semibold text-gray-900">₹{order.totalAmount?.toFixed(2) || '0.00'}</p>
                </div>
                
                {order.useMembership && (
                  <div className="flex justify-between mt-2 bg-green-50 p-2 rounded-lg">
                    <p className="text-green-700 text-sm">You saved with membership</p>
                    <p className="font-medium text-green-700">₹{order.serviceFee?.toFixed(2) || '0.00'}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-8 text-center text-sm text-gray-500 print:mt-12">
              <p>Thank you for choosing How2Apply!</p>
              <p className="mt-1">For any queries, please contact support@how2apply.net</p>
              <p className="font-medium mt-4 print:mt-8">Only Rs.100/- service charge per application</p>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6 mt-6 print:hidden">
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <Button 
                variant="outline" 
                onClick={() => navigate('/customer')}
              >
                Go to Dashboard
              </Button>
              <Button 
                onClick={handleTrackApplication}
                className='flex items-center justify-center'
              >
                Track Application
                <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
              </Button>
            </div>
            
            {/* Debug info for development */}
            <div className="mt-4 p-4 bg-gray-50 rounded-lg text-xs text-gray-600">
              <p><strong>Debug Info:</strong></p>
              <p>Order ID: {order?.id || 'Not found'}</p>
              <p>Order Status: {order?.status || 'Not found'}</p>
              <p>Service: {order?.serviceName || order?.serviceType || 'Not found'}</p>
            </div>
          </div>
        </Card>
      </motion.div>
    </div>
  );
};

export default PaymentReceipt;